// ═══════════════════════════════════════════════════════════════
// RollCall — Reusable PIN Pad Component
// 3x4 grid: 1-9, clear, 0, submit. Dot indicators.
// ═══════════════════════════════════════════════════════════════

import { useState, useCallback, useEffect } from 'react';
import { IconBackspace, IconCheck } from '@tabler/icons-react';

interface PinPadProps {
  onSubmit: (pin: string) => void;
  maxLength?: number;
  size?: 'large' | 'normal';
  disabled?: boolean;
}

export default function PinPad({ onSubmit, maxLength = 6, size = 'large', disabled = false }: PinPadProps) {
  const [pin, setPin] = useState('');
  const isLarge = size === 'large';
  const btnSize = isLarge ? 80 : 48;
  const fontSize = isLarge ? 28 : 18;
  const dotSize = isLarge ? 16 : 10;
  const gap = isLarge ? 12 : 8;

  const handleDigit = useCallback((d: string) => {
    if (disabled) return;
    setPin(prev => prev.length < maxLength ? prev + d : prev);
  }, [disabled, maxLength]);

  const handleClear = useCallback(() => {
    if (disabled) return;
    setPin('');
  }, [disabled]);

  const handleSubmit = useCallback(() => {
    if (disabled || !pin) return;
    onSubmit(pin);
    setPin('');
  }, [disabled, pin, onSubmit]);

  // Keyboard input support — listen for number keys, backspace, enter
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (disabled) return;
      if (e.key >= '0' && e.key <= '9') { handleDigit(e.key); e.preventDefault(); }
      else if (e.key === 'Backspace' || e.key === 'Delete') { handleClear(); e.preventDefault(); }
      else if (e.key === 'Enter') { handleSubmit(); e.preventDefault(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [disabled, handleDigit, handleClear, handleSubmit]);

  const btnStyle = (bg = 'rgba(255,255,255,0.1)', color = '#fff'): React.CSSProperties => ({
    width: btnSize, height: btnSize, borderRadius: isLarge ? 16 : 10,
    border: 'none', background: bg, color, fontSize, fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.12s',
    fontFamily: "'DM Mono', monospace", display: 'flex', alignItems: 'center', justifyContent: 'center',
    opacity: disabled ? 0.5 : 1,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: isLarge ? 24 : 16 }}>
      {/* PIN dots */}
      <div style={{ display: 'flex', gap: isLarge ? 12 : 8, height: isLarge ? 32 : 20, alignItems: 'center' }}>
        {Array.from({ length: maxLength }).map((_, i) => (
          <div key={i} style={{
            width: dotSize, height: dotSize, borderRadius: '50%',
            background: i < pin.length ? '#fff' : 'rgba(255,255,255,0.2)',
            transition: 'background 0.15s',
          }} />
        ))}
      </div>

      {/* Keypad grid */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(3, ${btnSize}px)`, gap }}>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(d => (
          <button key={d} style={btnStyle()} onClick={() => handleDigit(String(d))}
            onMouseDown={e => (e.currentTarget.style.transform = 'scale(0.95)')}
            onMouseUp={e => (e.currentTarget.style.transform = 'scale(1)')}
            onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}>
            {d}
          </button>
        ))}
        <button style={btnStyle('rgba(220,38,38,0.3)', '#fca5a5')} onClick={handleClear}>
          <IconBackspace size={fontSize * 0.7} />
        </button>
        <button style={btnStyle()} onClick={() => handleDigit('0')}>0</button>
        <button style={btnStyle('rgba(22,163,74,0.3)', '#86efac')} onClick={handleSubmit}>
          <IconCheck size={fontSize * 0.7} />
        </button>
      </div>
    </div>
  );
}
